<div class="container-fluid exec-summary">
  <span class="company-name-header">{{ tab_name }}</span>

  <!-- Spinner -->
  <div class="d-flex justify-content-center my-4" *ngIf="showSpinner">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Error -->
  <div class="alert alert-danger" *ngIf="loadingFailed">
    Failed to load innovation data. Please try again or contact
    <a href="mailto:info@quant-ip.com">info&#64;quant-ip.com</a>.
  </div>

  <!-- No data -->
  <div class="empty-state" *ngIf="showDataMissing && !showSpinner">
    <i class="bi bi-search empty-state-icon"></i>
    <h5>No Innovation Data Available</h5>
    <p>There haven't been any inventions published in the last 10 years to generate an innovation summary.</p>
  </div>

  <!-- Main content -->
  <ng-container *ngIf="companyData && snapshot && !showSpinner">

    <div class="overall_rating">

      <div class="overall_rating_text">
        <div class="innov_score_big_text">
          <span class="qip-score-title">
            Quant IP Innovation Score
            <button class="btn info-btn" (click)="openInfoPopup('company_innovation_score')">
              <i class="bi bi-info-circle"></i>
            </button>
          </span>

          <span [style.color]="competitivenessInfo(companyData.innovation_score).color" class="score-number">
            {{ companyData.innovation_score | nullableNumber:'1.0-1':'—' }}
          </span>
          <span class="score-outof">out of 100</span>
        </div>

        <span class="rating-detail" [style.color]="competitivenessInfo(companyData.innovation_score).color">
          {{ competitivenessInfo(companyData.innovation_score).text }}
          <br>
          <span class="among-text">
            among companies in {{ snapshot.peer_group_display || snapshot.peer_group_name || snapshot.peer_group }}
          </span>
          <br>

          <!-- Change inline -->
          <div class="change-inline" *ngIf="companyData.score_change !== null || companyData.pct_rank_climbed !== null">
            <span class="change-inline-title">Change in the last 3 months:</span>

            <span class="change-inline-item" *ngIf="companyData.score_change !== null">
              <div class="change-inline-row">
                <img [src]="flagImage(companyData.score_bracket ?? 3)" alt="" class="change-inline-arrow">
                <span class="change-inline-value" [style.color]="flagColor(companyData.score_bracket ?? 3)">
                  <span *ngIf="companyData.score_change! >= 0.05">+</span>{{ companyData.score_change |
                  nullableNumber:'1.0-1':'—' }}
                </span>
              </div>
              <span class="change-inline-label">Change in Score</span>
            </span>

            <span class="change-inline-divider"
              *ngIf="companyData.score_change !== null && companyData.rank_climbed !== null">|</span>

            <span class="change-inline-item" *ngIf="companyData.rank_climbed !== null">
              <div class="change-inline-row">
                <img [src]="flagImage(companyData.rank_bracket ?? 3)" alt="" class="change-inline-arrow">
                <span class="change-inline-value" [style.color]="flagColor(companyData.rank_bracket ?? 3)">
                  {{ rankChangeLabel(companyData.rank_climbed) }}
                </span>
              </div>
              <span class="change-inline-label">Change in Ranking</span>
            </span>
          </div>
        </span>
      </div>

      <!-- Signal card (public companies) -->
      <div class="signal-card" *ngIf="isPublic && companyData.signal"
        [style.border-color]="signalColor(companyData.signal)"
        [style.background-color]="signalCardBg(companyData.signal)">
        <div class="signal-card-body">
          <span class="signal-current-label">Current Signal <i class="bi bi-info-circle"
              matTooltipClass="multiline-tooltip" [matTooltip]="'Signal Thresholds:
BUY: Greater than 75
HOLD: Between 25 and 75
SELL: Less than 25'"></i> : </span>
          <span class="signal-options">
            <span class="signal-option" [style.color]="signalActiveColor(companyData.signal, 'BUY')">BUY</span>
            <span class="signal-separator">/</span>
            <span class="signal-option" [style.color]="signalActiveColor(companyData.signal, 'HOLD')">HOLD</span>
            <span class="signal-separator">/</span>
            <span class="signal-option" [style.color]="signalActiveColor(companyData.signal, 'SELL')">SELL</span>
          </span>
        </div>
        <div class="signal-card-desc" *ngIf="signalSentenceParts">
          <p [innerHTML]="signalSentenceParts[0]"></p>
          <p *ngIf="signalSentenceParts[1]">
            <span [innerHTML]="signalSentenceParts[1]"></span>
            <button class="btn info-btn" (click)="openInfoPopup('company_sector_perf')">
              <i class="bi bi-info-circle"></i>
            </button>
          </p>
          <p *ngIf="!signalSentenceParts[1]">
            <button class="btn info-btn" (click)="openInfoPopup('company_sector_perf')">
              <i class="bi bi-info-circle"></i>
            </button>
          </p>
        </div>

      </div>

      <!-- Signal sentence (private — score-based background, no signal badge) -->
      <div class="signal-card" *ngIf="!isPublic && signalSentenceParts"
        [style.border-color]="signalColor(companyData.signal)"
        [style.background-color]="signalCardBg(companyData.signal)">
        <div class="signal-card-desc">
          <p [innerHTML]="signalSentenceParts[0]"></p>
          <p [innerHTML]="signalSentenceParts[1]" *ngIf="signalSentenceParts[1]"></p>
        </div>
      </div>



      <!-- Summary table -->
      <div class="summary_table">
        <table class="table table-hover table-condensed">
          <thead>
            <tr>
              <th>Innovation Score Components</th>
              <th>Current</th>
              <th *ngIf="companyData.date_t1">3 Months Ago</th>
              <th>Peer Group Avg. <i class="bi bi-info-circle"
                  [matTooltip]="'Median values within the peer group for each indicator.'"></i></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>Portfolio Size (10y) <i class="bi bi-info-circle"
                  [matTooltip]="'Number of inventions published in the last 120 months.'"></i></th>
              <td style="font-weight: bold;" [style.color]="flagColor(companyData.invention_count_120m_bracket ?? 3)">
                {{ companyData.invention_count_120m | nullableNumber:'1.0-0':'—' }}
              </td>
              <td *ngIf="companyData.date_t1">{{ companyData.invention_count_120m_t1 | nullableNumber:'1.0-0':'—' }}
              </td>
              <td style="font-weight: bold;">{{ companyData.peer_avg_invention_count_120m | nullableNumber:'1.0-0':'—'
                }}
              </td>
            </tr>
            <tr>
              <th>Relative Growth (1y, %) <i class="bi bi-info-circle"
                  [matTooltip]="'% Year-over-year ratio. Number of inventions filed in the last 12 months divided by the prior 12 months.'"></i>
              </th>
              <td style="font-weight: bold;" [style.color]="flagColor(companyData.relative_growth_12m_bracket ?? 3)">
                <span *ngIf="companyData.relative_growth_12m != null">{{ companyData.relative_growth_12m >= 0.05 ? '+' : ''
                  }}{{ companyData.relative_growth_12m | nullableNumber:'1.0-1':'—' }}%</span><span
                  *ngIf="companyData.relative_growth_12m == null">—</span>
              </td>
              <td *ngIf="companyData.date_t1"><span *ngIf="companyData.relative_growth_12m_t1 != null">{{
                  companyData.relative_growth_12m_t1 >= 0.05 ? '+' : '' }}{{ companyData.relative_growth_12m_t1 |
                  nullableNumber:'1.0-1':'—' }}%</span><span *ngIf="companyData.relative_growth_12m_t1 == null">—</span>
              </td>
              <td style="font-weight: bold;"><span *ngIf="companyData.peer_avg_relative_growth_12m != null">{{
                  companyData.peer_avg_relative_growth_12m >= 0.05 ? '+' : '' }}{{ companyData.peer_avg_relative_growth_12m
                  | nullableNumber:'1.0-1':'—' }}%</span><span
                  *ngIf="companyData.peer_avg_relative_growth_12m == null">—</span></td>
            </tr>
            <tr>
              <th>Innovation Quality (1y) <i class="bi bi-info-circle"
                  [matTooltip]="'Average quality score of inventions published in the last 12 months.'"></i></th>
              <td style="font-weight: bold;" [style.color]="flagColor(companyData.quality_score_12m_bracket ?? 3)">
                {{ companyData.quality_score_12m | nullableNumber:'1.0-1':'—' }}
              </td>
              <td *ngIf="companyData.date_t1">{{ companyData.quality_score_12m_t1 | nullableNumber:'1.0-1':'—' }}</td>
              <td style="font-weight: bold;">{{ companyData.peer_avg_quality_score_12m | nullableNumber:'1.0-1':'—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- <div>
        <span style="font-size: small;">
          Quant IP Innovation Score is a composite metric based on the size, growth and quality of the innovation
          activity of a company, highlighting innovative companies that are most likely to outperform. The innovation
          score is normalized relative to the GICS Industry Peer Group of the company, with the best company in the peer
          group receiving a 100 score. <a href="javascript:void(0)" (click)="openInfoPopup('company_sector_perf')"
            class="backtest-link">Click here</a> to review our latest innovation score backtest.
        </span>
      </div> -->
      <!-- Top Competitors button -->
      <button class="btn top-competitors-btn" (click)="goToTopCompetitors()">
        <i class="bi bi-trophy"></i>
        View Top {{ isPublic ? 'Public' : 'Private' }} Competitors
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>
    <div style="font-size: small;margin-top:20px; margin-bottom:10px;">
      <span style="font-size: small;margin-top:20px; margin-bottom:10px;">
        Important Notice:
        This content is provided for general informational purposes only. It reflects a quantitative assessment of a
        company’s innovation activity based primarily on patent-related data and does not constitute investment advice,
        investment brokerage, portfolio management, a personal recommendation, or an offer or solicitation to buy, sell,
        or hold any security. The rating shown on this page, including any “BUY”, “HOLD”, or “SELL” label, is generated
        from a model that uses patent and related innovation signals only. It does not take into account any investor’s
        individual investment objectives, financial situation, tax position, liquidity needs, risk tolerance, existing
        holdings, or suitability requirements. The model does not incorporate all factors relevant to an investment
        decision, including valuation, financial performance, balance sheet strength, macroeconomic conditions,
        governance, litigation, competitive dynamics, or market sentiment.
      </span>
    </div>
  </ng-container>
</div>